// src/games/Quiz/api.ts
import type { iQuiz } from '../../interfaces/quiz';
import { apiNode } from '../../services/api';
import { RANKING_TIPO, createRankingPosition } from '../core/api';
import { finishTempo } from '../core/session/time';
import type { FinishResult } from '../core/session/useGameSession';
import { POINTS_PER_ANSWER, QuizAnswer, isQuizComplete, quizCorrectCount, quizPoints, readQuizAnswers } from './rules';

export interface QuizProgressRow {
  respondidos: unknown;
  pontuacao: number | null;
  tempo: number | null;
}

export interface SaveQuizArgs {
  userId: number;
  jogoId: number;
  answers: QuizAnswer[];
  seconds: number;
}

export interface QuizLoad {
  questions: iQuiz[];
  answers: QuizAnswer[];
  savedSeconds: number;
  finished: FinishResult | null;
}

export async function getQuizQuestions(jogoId: number): Promise<iQuiz[]> {
  const { data } = await apiNode.get(`/list_questions/${jogoId}`);
  return Array.isArray(data) ? data : [];
}

export async function getQuizProgress(userId: number, jogoId: number): Promise<QuizProgressRow | null> {
  const { data } = await apiNode.get(`/quiz_progresso/${userId}`, { params: { jogos_id: jogoId } });
  return Array.isArray(data) && data[0] ? data[0] : null;
}

/** `pontuacao` do progresso = nº de acertos (como o app). */
export async function saveQuizProgress({ userId, jogoId, answers, seconds }: SaveQuizArgs): Promise<void> {
  await apiNode.post('/create_progress_quiz', {
    id_jogo: jogoId,
    id_user: userId,
    respondidos: answers,
    pontuacao: quizCorrectCount(answers),
    tempo: Math.max(0, Math.round(seconds)),
  });
}

/** Fim: progresso e depois o ranking tipo 6 com acertos × 10. */
export async function finishQuiz(args: SaveQuizArgs): Promise<void> {
  const tempo = finishTempo(args.seconds);
  await saveQuizProgress({ ...args, seconds: tempo });
  await createRankingPosition({
    id_jogo: args.jogoId,
    id_user: args.userId,
    id_tipo: RANKING_TIPO.quiz,
    pontuacao: quizPoints(args.answers),
    tempo,
  });
}

export function quizResult(questions: iQuiz[], answers: QuizAnswer[]): FinishResult {
  return {
    points: quizPoints(answers),
    maxPoints: questions.length * POINTS_PER_ANSWER,
    correct: quizCorrectCount(answers),
    total: questions.length,
  };
}

/** `null` = quiz sem perguntas. Erro ao ler o progresso SOBE (não abre em branco). */
export async function loadQuizGame(jogoId: number, userId?: number): Promise<QuizLoad | null> {
  const [questions, progress] = await Promise.all([
    getQuizQuestions(jogoId),
    userId !== undefined ? getQuizProgress(userId, jogoId) : Promise.resolve(null),
  ]);
  if (questions.length === 0) return null;
  const answers = readQuizAnswers(progress?.respondidos, questions);
  return {
    questions,
    answers,
    savedSeconds: Math.max(0, Math.round(progress?.tempo ?? 0)),
    finished: isQuizComplete(answers, questions) ? quizResult(questions, answers) : null,
  };
}
